import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { ExpenseService, TokenStorageService } from '../../../core/services';
import {
  appendUniqueById,
  createListPaginationState,
  hasMoreByTotal,
  resolveRequestPage,
  resetListPaginationState,
  updateAfterFetch
} from '../../../core/utils/list-pagination.util';
import {
  EntityListComponent,
  EntityListMenuAction,
  TableHeaderAndValueKeySets
} from '../../../core/ui/entity-list/entity-list.component';
import { NewEditExpenseComponent } from './new-edit-expense/new-edit-expense.component';
import { ConfirmActionModalComponent } from '../../../core/ui/confirm-action-modal/confirm-action-modal.component';
import { TranslatePipe } from '../../../core/pipes/translate.pipe';

interface AuditActor {
  id?: number;
  organizationName?: string;
  name?: string;
  username?: string;
  email?: string;
}

interface ExpenseItem {
  id: number;
  txnNo?: number | string;
  house?: {
    id?: number;
    houseName?: string;
    houseNo?: string;
  };
  flat?: {
    id?: number;
    flatName?: string;
    floorName?: string;
    house?: {
      id?: number;
      houseName?: string;
    };
  };
  expenseHead?: {
    id?: number;
    name?: string;
  };
  remarks?: string;
  expenseAmount?: number;
  expenseDate?: string;
  expenseMonth?: number;
  expenseYear?: number | string;
  status: number;
  createdOn?: string;
  updatedOn?: string | null;
  createdBy?: number;
  updatedBy?: number;
  createdByUser?: AuditActor;
  updatedByUser?: AuditActor;
  expenseHistoryList?: ExpenseHistoryItem[];
}

interface ExpenseHistoryItem {
  id?: number;
  txnNo?: number | string;
  remarks?: string;
  expenseAmount?: number;
  expenseDate?: string;
  expenseMonth?: number;
  expenseYear?: number | string;
  createdOn?: string;
  status?: number;
  createdBy?: number;
  updatedBy?: number;
  expenseHead?: {
    id?: number;
    name?: string;
  };
  createdByUser?: AuditActor;
  updatedByUser?: AuditActor;
}

interface ExpenseRow extends ExpenseItem {
  txnNoLabel: string;
  flatTopLine: string;
  flatBottomLine: string;
  expenseHeadLabel: string;
  expenseMonthLabel: string;
  expenseDateLabel: string;
  expenseAmountLabel: string;
  statusLabelText: string;
  hasHistoryAction: string | null;
}

@Component({
  selector: 'app-expense-list',
  standalone: true,
  imports: [CommonModule, EntityListComponent, NewEditExpenseComponent, ConfirmActionModalComponent, TranslatePipe],
  templateUrl: './expense-list.component.html',
  styleUrls: ['./expense-list.component.css', './../../../../CSSFiles/BTN_Style.css']
})
export class ExpenseListComponent implements OnInit {
  isAuthenticated = false;
  isLoading = false;
  isLoadingMore = false;
  expenseList: ExpenseRow[] = [];
  error: string | null = null;
  status: string | null = null;
  filters: Record<string, any> = {};
  private readonly paginationState = createListPaginationState();

  selectedExpense: ExpenseItem | null = null;
  pendingDeleteExpense: ExpenseItem | null = null;
  selectedExpenseHistory: ExpenseHistoryItem[] = [];
  selectedHistoryItem: ExpenseHistoryItem | null = null;
  isDetailsOpen = false;
  isHistoryOpen = false;
  isHistoryDetailsOpen = false;
  isEditOpen = false;
  isDeleting = false;

  tableHeaderAndValueKeySets: TableHeaderAndValueKeySets = {
    colorKey: 'status',
    'table.flat': {
      topLineKey: 'flatTopLine',
      bottomLineKyes: ['flatBottomLine']
    },
    'table.expenseHead': 'expenseHeadLabel',
    'table.expenseMonth': 'expenseMonthLabel',
    'table.expenseDate': 'expenseDateLabel',
    'table.amount': 'expenseAmountLabel'
  };
  private readonly activeExpenseMenuActions: EntityListMenuAction[] = [
    { key: 'edit', label: 'action.edit' },
    { key: 'delete', label: 'action.delete', danger: true, requiredService: 'EXPENSE_DELETE' },
    { key: 'history', label: 'action.history', visibleWhen: 'hasHistoryAction', requiredService: 'EXPENSE_CREATE' }
  ];
  private readonly deletedExpenseMenuActions: EntityListMenuAction[] = [
    { key: 'history', label: 'action.history', visibleWhen: 'hasHistoryAction' }
  ];

  get deletedMode(): boolean {
    return Number(this.filters?.['status']) === 0;
  }

  get expenseMenuActions(): EntityListMenuAction[] {
    return this.deletedMode ? this.deletedExpenseMenuActions : this.activeExpenseMenuActions;
  }

  constructor(
    private readonly expenses: ExpenseService,
    private readonly tokens: TokenStorageService
  ) {}

  ngOnInit(): void {
    this.isAuthenticated = this.tokens.hasActiveSession;
    if (!this.isAuthenticated) {
      this.error = 'Please log in first.';
      return;
    }

    this.status = 'Loading expense list...';
    this.fetchExpenses(true);
  }

  get hasMoreExpenses(): boolean {
    return hasMoreByTotal(this.expenseList, this.paginationState);
  }

  fetchExpenses(reset = true): void {
    if ((reset && this.isLoading) || (!reset && (this.isLoading || this.isLoadingMore || !this.hasMoreExpenses))) {
      return;
    }

    if (reset) {
      this.isLoading = true;
      this.error = null;
      this.expenseList = [];
      resetListPaginationState(this.paginationState);
    } else {
      this.isLoadingMore = true;
    }

    const filters = { ...this.filters };
    if (!Object.prototype.hasOwnProperty.call(filters, 'status')) {
      filters['status'] = 1;
    }
    const requestPage = resolveRequestPage(this.paginationState, reset);
    this.expenses.list({
      pagination: this.paginationState.pagination,
      pageNo: requestPage,
      rowCount: this.paginationState.rowCount,
      withTotals: true,
      filters
    })
      .pipe(finalize(() => {
        if (reset) {
          this.isLoading = false;
        } else {
          this.isLoadingMore = false;
        }
      }))
      .subscribe({
        next: (res: any) => {
          const data = Array.isArray(res?.data) ? res.data : [];
          const nextRows: ExpenseRow[] = data.map((item: ExpenseItem) => {
            const houseName = item.house?.houseName || item.flat?.house?.houseName || '';
            const houseNo = item.house?.houseNo || '';
            const topLine = item.flat?.flatName || houseName || 'N/A';
            const bottomParts = item.flat?.id
              ? [item.flat?.floorName, houseName].filter((v) => !!v)
              : [houseNo, 'House Expense'].filter((v) => !!v);
            const historyCount = Array.isArray(item.expenseHistoryList) ? item.expenseHistoryList.length : 0;
            return {
              ...item,
              txnNoLabel: item.txnNo ? `${item.txnNo}` : 'N/A',
              flatTopLine: topLine,
              flatBottomLine: bottomParts.join(' | ') || 'N/A',
              expenseHeadLabel: item.expenseHead?.name || 'N/A',
              expenseMonthLabel: this.expenseMonthLabel(item.expenseMonth, item.expenseYear),
              expenseDateLabel: this.formatDate(item.expenseDate),
              expenseAmountLabel: this.formatAmount(Number(item.expenseAmount ?? 0)),
              statusLabelText: item.status === 1 ? 'Active' : 'Inactive',
              hasHistoryAction: historyCount > 1 ? '1' : null
            };
          });
          updateAfterFetch(this.paginationState, res?.totalRows, requestPage, nextRows.length);
          this.expenseList = reset ? nextRows : appendUniqueById(this.expenseList, nextRows);

          this.status = `Total expenses: ${this.paginationState.totalRows || this.expenseList.length}`;
        },
        error: (err: any) => {
          this.error = err?.message || 'Error fetching expenses.';
        }
      });
  }

  setFilters(filters: Record<string, any>): void {
    this.filters = { ...filters };
    this.fetchExpenses(true);
  }

  onLoadMoreFromList(): void {
    this.fetchExpenses(false);
  }

  openCreateModal(): void {
    this.selectedExpense = null;
    this.isEditOpen = true;
  }

  onCardClickFromList(item: Record<string, any>): void {
    this.selectedExpense = item as ExpenseItem;
    this.isDetailsOpen = true;
  }

  onEditClickFromList(item: Record<string, any>): void {
    this.selectedExpense = item as ExpenseItem;
    this.isEditOpen = true;
  }

  onDeleteClickFromList(item: Record<string, any>): void {
    if (this.isDeleting) {
      return;
    }
    this.pendingDeleteExpense = item as ExpenseItem;
  }

  closeDeleteModal(): void {
    if (this.isDeleting) {
      return;
    }
    this.pendingDeleteExpense = null;
  }

  confirmDeleteExpense(): void {
    const expense = this.pendingDeleteExpense;
    if (!expense || this.isDeleting) {
      return;
    }

    this.isDeleting = true;
    this.error = null;

    this.expenses.delete({ id: expense.id })
      .pipe(finalize(() => (this.isDeleting = false)))
      .subscribe({
        next: () => {
          this.pendingDeleteExpense = null;
          this.fetchExpenses(true);
        },
        error: (err: any) => {
          this.pendingDeleteExpense = null;
          this.error = err?.message || 'Failed to delete expense.';
        }
      });
  }

  closeDetailsModal(): void {
    this.isDetailsOpen = false;
  }

  closeHistoryModal(): void {
    this.isHistoryOpen = false;
    this.selectedExpenseHistory = [];
    this.selectedHistoryItem = null;
    this.isHistoryDetailsOpen = false;
  }

  openHistoryDetails(item: ExpenseHistoryItem): void {
    this.selectedHistoryItem = item;
    this.isHistoryDetailsOpen = true;
  }

  closeHistoryDetailsModal(): void {
    this.isHistoryDetailsOpen = false;
    this.selectedHistoryItem = null;
  }

  closeEditModal(): void {
    this.isEditOpen = false;
    this.selectedExpense = null;
  }

  onEditSave(): void {
    this.isEditOpen = false;
    this.selectedExpense = null;
    this.fetchExpenses(true);
  }

  onMenuAction(event: { actionKey: string; item: Record<string, any> }): void {
    if (event.actionKey !== 'history') {
      return;
    }

    const expense = event.item as ExpenseItem;
    this.selectedExpense = expense;
    this.selectedExpenseHistory = Array.isArray(expense.expenseHistoryList) ? expense.expenseHistoryList : [];
    this.isHistoryOpen = true;
  }

  formatDate(value: string | null | undefined): string {
    if (!value) {
      return 'N/A';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return `${value}`;
    }

    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) {
      return 'N/A';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'N/A';
    }

    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    const rawHours = date.getHours();
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    const period = rawHours >= 12 ? 'pm' : 'am';
    const hours12 = rawHours % 12 || 12;
    const hours = `${hours12}`.padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes} ${period}`;
  }

  expenseMonthLabel(month: number | undefined, year: number | string | undefined): string {
    const monthNo = Number(month);
    if (!Number.isFinite(monthNo) || monthNo < 1 || monthNo > 12) {
      return 'N/A';
    }
    const monthName = new Date(2000, monthNo - 1, 1).toLocaleString('en-US', { month: 'long' });
    return `${monthName} ${year ?? ''}`.trim();
  }

  private formatAmount(value: number): string {
    return `${Number(value ?? 0).toLocaleString('en-US')}`;
  }

  historyByLabel(item: ExpenseHistoryItem): string {
    if (item.updatedBy != null || item.updatedByUser) {
      return this.actorLabel(item.updatedByUser, item.updatedBy);
    }
    return this.actorLabel(item.createdByUser, item.createdBy);
  }

  hasRecordedUpdate(item: { createdOn?: string | null; updatedOn?: string | null }): boolean {
    const updatedOn = `${item.updatedOn ?? ''}`.trim();
    if (!updatedOn) {
      return false;
    }
    const createdOn = `${item.createdOn ?? ''}`.trim();
    return !createdOn || updatedOn !== createdOn;
  }

  hasUpdatedActor(item: { updatedBy?: number | null; updatedByUser?: AuditActor | null }): boolean {
    return item.updatedBy != null || !!item.updatedByUser;
  }

  actorLabel(user: AuditActor | null | undefined, fallbackId?: number | null): string {
    const label = `${user?.organizationName ?? user?.name ?? user?.username ?? user?.email ?? ''}`.trim();
    if (label) {
      return label;
    }
    return fallbackId != null ? `User #${fallbackId}` : 'N/A';
  }

  houseLabel(expense: ExpenseItem | null | undefined): string {
    return `${expense?.house?.houseName ?? expense?.flat?.house?.houseName ?? ''}`.trim() || 'N/A';
  }
}
